import { type ChangeEvent, type FormEvent, type KeyboardEvent, useRef, useState } from 'react';
import styled from 'styled-components';

import { buttonShadows } from '../../cdl/Button/components/buttonShadows';
import { translate } from '../../common/helpers/translate.helper';
import { IconArrowUp } from '../../common/icons/cdl/ArrowUp';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';

import { ActivityInputTextArea } from './ActivityInputTextArea';
import { useMessageCreate } from './useMessageCreate';

const ActivityInputCard = styled(Box)`
    box-shadow:
        ${theme.shadows.medium},
        0 0 0 1px ${theme.colors.border.subtle};
    transition: box-shadow 75ms ease-in;

    &:focus-within {
        box-shadow:
            ${theme.shadows.large},
            0 0 0 1px ${theme.colors.border.subtle};
    }
`;

const SendButton = styled.button`
    align-items: center;
    background: linear-gradient(
        0deg,
        ${theme.colors.accent.emphasis},
        ${theme.colors.accent.emphasis},
        ${theme.colors.effects.accentGradientStop}
    );
    background-position: 0% 0%;
    background-size: 200% 200%;
    border: none;
    border-radius: 50%;
    box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorder};
    color: ${theme.colors.foreground.onEmphasis};
    cursor: pointer;
    display: flex;
    height: 32px;
    justify-content: center;
    outline: none;
    padding: 0;
    transition:
        background-position 100ms ease-out,
        box-shadow 100ms ease-in-out,
        color 100ms ease-out;
    width: 32px;

    &:hover:not(:disabled) {
        background-position: 100% 100%;
        box-shadow: ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover}, ${buttonShadows.dropHover};
    }

    &:focus:not(:disabled) {
        box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover},
            ${buttonShadows.dropHover};
    }

    &:active:not(:disabled) {
        background: ${theme.colors.accent.foreground};
        box-shadow: ${theme.shadows.focus}, ${buttonShadows.emphDrop}, ${buttonShadows.emphBorderHover},
            ${buttonShadows.dropHover};
    }

    &:disabled {
        background: transparent;
        box-shadow: none;
        color: ${theme.colors.foreground.subtle};
        cursor: default;
    }
`;

interface OfferActivityInputProps {
    offerId: string;
}

export const OfferActivityInput = ({ offerId }: OfferActivityInputProps) => {
    const [message, setMessage] = useState('');
    const messageMutation = useMessageCreate();
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const inputInvalid = message.length === 0 || messageMutation.isPending;

    const sendMessage = async (event: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>) => {
        event.preventDefault();

        if (inputInvalid) {
            return;
        }

        try {
            await messageMutation.mutateAsync({ offerId, message });
        } catch {
            return;
        }

        setMessage('');
        inputRef.current?.focus();
    };

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const handleKeySubmit = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            sendMessage(event);
        }
    };

    return (
        <form onSubmit={sendMessage}>
            <ActivityInputCard
                position="relative"
                backgroundColor="background.default"
                borderRadius={6}
                padding="12px 16px"
                paddingRight="48px"
            >
                <ActivityInputTextArea
                    inputRef={inputRef}
                    onChange={handleChange}
                    value={message}
                    placeholder={translate('placeholder.leaveAComment')}
                    onKeyDown={handleKeySubmit}
                />
                <Box position="absolute" bottom="10px" right="12px">
                    <SendButton type="submit" disabled={inputInvalid}>
                        <IconArrowUp width={16} height={16} />
                    </SendButton>
                </Box>
            </ActivityInputCard>
        </form>
    );
};
